import {
    createWebsite_api,
    deleteWebsite_api,
    getWebsite_api,
    getWebsites_api,
    updateWebsite_api,
    WebsiteBuilderData,
    WebsiteBuilderDataPayload,
    getWebsitesGallery_api
} from 'api/websiteBuilderApi';
import { PlatformItem } from 'elements/PlatformLink';
import { TabItem } from 'elements/TabsSymphony';
import Colors from 'modules/Colors';
import { RecordDetails, TourDate } from 'modules/Types';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import useCurrentBrand, { BrandExistsState } from './useCurrentBrand';
import {
    SocialPlatform,
    STREAMING_PLATFORMS_INFO,
    StreamingPlatform,
    WebsiteBuilderTemplateType,
    WEBSITE_TEMPLATE_INFO,
    WEBSITE_TEMPLATE_CATEGORIES
} from 'modules/Const';
import dayjs from 'dayjs';
import { getPalette } from 'react-palette';
import { WebsiteType } from 'modules/Const';
import { SymphonyLogoStyleKey } from 'pages/WebsitePage/components/SymphonyLogoEditorCard';
import { calculateThemeColors, convertTwitterUrlToXUrl } from 'modules/Utils';
import { track } from 'analytics';
import useCurrentUser from './useCurrentUser';

const DEFAULT_COLORS = {
    backgroundColor: '#8800ff',
    secondaryColor: '#ab66ec',
    textColor: '#FFFFFF',
}

export interface WebsiteBuilderTemplate {
    type: WebsiteBuilderTemplateType;
    title: string;
    websiteContent: WebsiteBuilderContent[];
    websiteStyle: WebsiteBuilderStyle;
}

export enum WebsiteTextAlign {
    LEFT = 'left',
    CENTER = 'center',
    RIGHT = 'right',
}

export enum PresaveWebsiteBuilderStyle {
    BOXES = 'boxes',
    STACKED = 'stacked',
    TRADITIONAL = 'traditional',
}

export enum PresavePlatform {
    SPOTIFY = 'spotify',
    APPLE_MUSIC = 'apple_music',
    AUDIOMACK = 'audiomack',
    SOUNDCLOUD = 'soundcloud',
    DEEZER = 'deezer',
}

export interface PresavePlatformItem {
    name: PresavePlatform;
    isEnabled: boolean;
}

export interface PresaveWebsiteType {
    /** Boost settings */
    boosts: {
        /** Enable automatic presaves */
        auto_presaves: boolean;
        /** Enable boost follows */
        boost_follows: boolean;
        /** Enable email collection */
        collect_emails: boolean;
    };
    /** Enabled platforms */
    platforms: PresavePlatformItem[]
    /** URL to redirect to */
    redirect_url?: string;
    /** Whether to redirect to custom webpage */
    redirect_to_custom_webpage?: boolean;
    /** Release date */
    release_date: string;
    /** Release URL with UPC/ISRC */
    release_url_upc_isrc?: string;
    /** When content was saved */
    saved_at?: string;
    /** Timezone setting */
    timezone?: string;
    /** Whether the release is a real-time release */
    is_real_time_release?: boolean;
}

export enum WebsiteStatus {
    DRAFT = 'DRAFT',
    PUBLISHED = 'PUBLISHED',
    ARCHIVED = 'ARCHIVED'
}

export enum WebsiteBuilderContentType {
    COLLECT_DATA = 'collectData',
    DIVIDER = 'divider',
    EXTERNAL_LINKS = 'externalLinks',
    IMAGE = 'image',
    MUSIC = 'music',
    PAYMENT = 'payment',
    PRESAVE = 'presave',
    SOCIAL_PLATFORMS = 'socialPlatforms',
    STREAMING_PLATFORMS = 'streamingPlatforms',
    TABS = 'tabs',
    TEXT = 'text',
    TOUR = 'tour',
    VIDEO = 'video',
}

interface WebsiteBuilderPlatformItem {
    name: string;
    link: string;
    isEnabled: boolean;
    placeholder: string;
}

export const STREAMING_PLATFORMS_WEBSITE_CONTENT: WebsiteBuilderPlatformItem[] = [
    {
        name: 'youtube',
        link: 'https://www.youtube.com',
        isEnabled: true,
        placeholder: 'YouTube Link',
    },
    {
        name: 'spotify',
        link: 'https://www.spotify.com',
        isEnabled: true,
        placeholder: 'Spotify Link',
    },
    {
        name: 'deezer',
        link: 'https://www.deezer.com',
        isEnabled: true,
        placeholder: 'Deezer Link',
    },
    {
        name: 'apple_music',
        link: 'https://www.apple.com/music',
        isEnabled: true,
        placeholder: 'Apple Music Link',
    },
    {
        name: 'amazon_music',
        link: 'https://www.amazon.com/music',
        isEnabled: true,
        placeholder: 'Amazon Music Link',
    },
    {
        name: 'soundcloud',
        link: 'https://www.soundcloud.com',
        isEnabled: true,
        placeholder: 'SoundCloud Link',
    },
    {
        name: 'tidal',
        link: 'https://www.tidal.com',
        isEnabled: true,
        placeholder: 'Tidal Link',
    },
    {
        name: 'audiomack',
        link: 'https://www.audiomack.com',
        isEnabled: true,
        placeholder: 'Audiomack Link',
    },
    {
        name: 'anghami',
        link: 'https://www.anghami.com',
        isEnabled: true,
        placeholder: 'Anghami Link',
    },
]

export const SOCIAL_PLATFORMS_WEBSITE_CONTENT: WebsiteBuilderPlatformItem[] = [
    {
        name: 'instagram',
        link: 'https://www.instagram.com',
        isEnabled: true,
        placeholder: 'Instagram Link',
    },
    {
        name: 'tiktok',
        link: 'https://www.tiktok.com',
        isEnabled: true,
        placeholder: 'TikTok Link',
    },
    {
        name: 'facebook',
        link: 'https://www.facebook.com',
        isEnabled: true,
        placeholder: 'Facebook Link',
    },
    {
        name: 'x',
        link: 'https://www.x.com',
        isEnabled: true,
        placeholder: 'X Link',
    },
    {
        name: 'youtube',
        link: 'https://www.youtube.com',
        isEnabled: true,
        placeholder: 'YouTube Link',
    },
    {
        name: 'linkedin',
        link: 'https://www.linkedin.com',
        isEnabled: true,
        placeholder: 'LinkedIn Link',
    },
    {
        name: 'email',
        link: 'mailto:',
        isEnabled: true,
        placeholder: 'Email',
    },
]

export enum CollectDataInputType {
    EMAIL_PHONE = 'Email / Phone',
    TEXT = 'Text',
    NUMBER = 'Number',
}

export interface CollectDataContentType {
    title: string;
    buttonText: string;
    postSubscribeMessage: string;
    isBothEmailPhone: boolean;
    isShowTitles: boolean;
    info: {
        title: string;
        placeholder?: string;
        type: CollectDataInputType;
        isRequired: boolean;
    }[];
}

export interface TabContentType {
    title: string;
    activeTab: string;
    tabs: TabItem[];
}

export interface TextContentType {
    title: string;
    isSubtitle: boolean;
    subtitle: string;
}

export enum ExternalLinksStyle {
    BOXES = 'boxes',
    STACKED_SQUARES = 'stackedSquares',
    STACKED_ROUNDED = 'stackedRounded',
    HYPERLINKS = 'hyperlinks',
}

export interface ExternalLinksContentType {
    title: string;
    platforms: PlatformItem[];
    style: ExternalLinksStyle;
}

export interface SocialPlatformsContentType {
    title: string;
    platforms: PlatformItem[];
}

export enum StreamingPlatformsStyle {
    BOXES = 'boxes',
    STACKED = 'stacked',
    STACKED_BOXES = 'stackedBoxes',
    ICONS = 'icons',
    TRADITIONAL = 'traditional',
}

export interface StreamingPlatformsContentType {
    title: string;
    platforms: PlatformItem[];
    style: StreamingPlatformsStyle;
}

export enum ImageContentStyle {
    SQUARE = 'square',
    ROUNDED = 'rounded',
    CIRCLE = 'circle',
    SMALL_CIRCLE = 'smallCircle',
    FULL_SCREEN = 'fullScreen',
}

export interface ImageContentType {
    title: string;
    image: string;
    style: ImageContentStyle;
    isTitle: boolean;
    isSubtitle: boolean;
    subtitle: string;
}

export enum VideoContentStyle {
    SQUARE = 'square',
    ROUNDED = 'rounded',
    CIRCULAR = 'circular',
    FULL_SCREEN = 'fullScreen',
}

export interface VideoContentType {
    title: string;
    video: string;
    thumbnail: string;
    style: VideoContentStyle;
}

export enum MusicContentStyle {
    ARTWORK = 'artwork',
    PLAYER = 'player',
    FULL_SCREEN = 'fullScreen',
    RECORD_PLAYER = 'recordPlayer',
}

export interface MusicContentType {
    title: string;
    selectedMusic?: RecordDetails;
    style: MusicContentStyle;
}

export interface PresaveContentType {
    title: string;
    style: PresaveWebsiteBuilderStyle;
    hideReleaseDate?: boolean;
    presave: PresaveWebsiteType;
}

export enum DividerStyle {
    SOLID = 'solid',
    DASHED = 'dashed',
    SOLID_PADDING = 'solidPadding',
    DASHED_PADDING = 'dashedPadding',
}

export interface DividerContentType {
    title: string;
    style: DividerStyle;
}

export interface PaymentContentType {
    title: string;
}

export enum TourStyle {
    BOXES = 'boxes',
    STACKED = 'stacked',
}

export interface TourContentType {
    title: string;
    tourDates: TourDate[];
    hidePastTourDates: boolean;
    style: TourStyle;
}

export interface WebsiteBuilderContent {
    id: string;
    type: WebsiteBuilderContentType;
    contentIndex?: number; // NOTE: not sure if needed
    tabId?: string;

    contentBackgroundColor?: string;
    isContentBackgroundColorEnabled?: boolean;
    contentSecondaryColor?: string;
    isContentSecondaryColorEnabled?: boolean;

    contentTextColor?: string;
    isContentTextColorEnabled?: boolean;
    contentTextSecondaryColor?: string;
    isContentTextSecondaryColorEnabled?: boolean;

    collectDataContent?: CollectDataContentType;
    dividerContent?: DividerContentType;
    externalLinksContent?: ExternalLinksContentType;
    imageContent?: ImageContentType;
    musicContent?: MusicContentType;
    paymentContent?: PaymentContentType;
    presaveContent?: PresaveContentType;
    socialPlatformsContent?: SocialPlatformsContentType;
    streamingPlatformsContent?: StreamingPlatformsContentType;
    tabsContent?: TabContentType;
    textContent?: TextContentType;
    tourContent?: TourContentType;
    videoContent?: VideoContentType;
}

interface WebsiteBuilderContent_collectDataExists extends WebsiteBuilderContent {
    type: WebsiteBuilderContentType.COLLECT_DATA;
    collectDataContent: CollectDataContentType;
}

interface WebsiteBuilderContent_dividerExists extends WebsiteBuilderContent {
    type: WebsiteBuilderContentType.DIVIDER;
    dividerContent: DividerContentType;
}

interface WebsiteBuilderContent_externalLinksExists extends WebsiteBuilderContent {
    type: WebsiteBuilderContentType.EXTERNAL_LINKS;
    externalLinksContent: ExternalLinksContentType;
}

interface WebsiteBuilderContent_imageExists extends WebsiteBuilderContent {
    type: WebsiteBuilderContentType.IMAGE;
    imageContent: ImageContentType;
}

interface WebsiteBuilderContent_musicExists extends WebsiteBuilderContent {
    type: WebsiteBuilderContentType.MUSIC;
    musicContent: MusicContentType;
}

interface WebsiteBuilderContent_paymentExists extends WebsiteBuilderContent {
    type: WebsiteBuilderContentType.PAYMENT;
    paymentContent: PaymentContentType;
}

interface WebsiteBuilderContent_presaveExists extends WebsiteBuilderContent {
    type: WebsiteBuilderContentType.PRESAVE;
    presaveContent: PresaveContentType;
}

interface WebsiteBuilderContent_socialPlatformsExists extends WebsiteBuilderContent {
    type: WebsiteBuilderContentType.SOCIAL_PLATFORMS;
    socialPlatformsContent: SocialPlatformsContentType;
}

interface WebsiteBuilderContent_streamingPlatformsExists extends WebsiteBuilderContent {
    type: WebsiteBuilderContentType.STREAMING_PLATFORMS;
    streamingPlatformsContent: StreamingPlatformsContentType;
}

interface WebsiteBuilderContent_tabsExists extends WebsiteBuilderContent {
    type: WebsiteBuilderContentType.TABS;
    tabsContent: TabContentType;
}

interface WebsiteBuilderContent_textExists extends WebsiteBuilderContent {
    type: WebsiteBuilderContentType.TEXT;
    textContent: TextContentType;
}

interface WebsiteBuilderContent_tourExists extends WebsiteBuilderContent {
    type: WebsiteBuilderContentType.TOUR;
    tourContent: TourContentType;
}

interface WebsiteBuilderContent_videosExists extends WebsiteBuilderContent {
    type: WebsiteBuilderContentType.VIDEO;
    videoContent: VideoContentType;
}

export interface WebsiteBuilderStyle {
    backgroundColor: string;
    secondaryColor: string;
    textColor: string;
    fontFamily: string;
    textAlign: WebsiteTextAlign;
    isSymphonyLogoEnabled: boolean;
    symphonyLogoStyle: SymphonyLogoStyleKey;
}

/** Store interface for website builder state */
interface WebsiteBuilderStore {
    // State
    websites: WebsiteBuilderData[];
    websitesGallery: WebsiteBuilderData[];
    websiteBuilderData: WebsiteBuilderData | null;
    websiteContent: WebsiteBuilderContent[];
    websiteStyle: WebsiteBuilderStyle;

    isLoadingWebsiteBuilder: boolean;
    isWebsitesInitialized: boolean;
    isPublishingWebsite: boolean;
    isLoadingWebsitesGallery: boolean;

    websiteSlugText: string;

    showCreateNewWebsiteModal: boolean;
    showCreateNewWebsiteModalV2: boolean;
    initialWebsiteType: WebsiteType | null;
    initialRecord: RecordDetails | null;

    selectedWebsiteTemplate: WebsiteBuilderTemplateType | null;
    selectedTemplateCategory: string;

    // Actions
    setWebsites: (websites: WebsiteBuilderData[]) => void;
    setWebsitesGallery: (websites: WebsiteBuilderData[]) => void;
    setWebsiteContent: (content: WebsiteBuilderContent[]) => void;
    setWebsiteStyle: (style: WebsiteBuilderStyle) => void;

    setIsLoadingWebsiteBuilder: (isLoading: boolean) => void;
    setIsPublishingWebsite: (isPublishing: boolean) => void;
    setIsLoadingWebsitesGallery: (isLoading: boolean) => void;

    setSelectedTemplateCategory: (templateCategory: string) => void;

    setWebsiteSlugText: (slug: string) => void;
    setShowCreateNewWebsiteModal: (isShow: boolean) => void;
    setShowCreateNewWebsiteModalV2: (isShow: boolean) => void;
    setInitialWebsiteType: (websiteType: WebsiteType | null) => void;
    setInitialRecord: (record: RecordDetails | null) => void;
    setSelectedWebsiteTemplate: (template: WebsiteBuilderTemplateType) => void;
    getInitialWebsiteStyle: (image?: string) => Promise<void>;
    resetWebsiteBuilder: () => void;
    needsPaidBrand: () => boolean;
    canSaveOrPublishWebsite: () => boolean;

    isPublished: () => boolean;

    // Methods
    getWebsite: ({ websiteId, websiteSlug, brandSlug }: { websiteId?: number, websiteSlug?: string, brandSlug?: string }) => Promise<WebsiteBuilderData | undefined>;
    getWebsites: ({ brandId, isAllWebsites, includeBrand, resetInitializeWebsites }: { brandId?: number, isAllWebsites?: boolean, includeBrand?: boolean, resetInitializeWebsites?: boolean }) => Promise<WebsiteBuilderData[]>;
    createWebsite: () => Promise<WebsiteBuilderData | null>;
    updateWebsite: (options?: {
        websiteId?: number;
        websiteBuilderPayload?: WebsiteBuilderDataPayload;
    }) => Promise<WebsiteBuilderData | null>;
    deleteWebsite: (websiteId: number) => Promise<WebsiteBuilderData | null>;
    getSocialPlatforms: () => PlatformItem[];

    getWebsitesGallery: ({ websiteIds }: { websiteIds?: number[] }) => Promise<WebsiteBuilderData[]>;

    cloneWebsite: ({ websiteContent, websiteStyle }: { websiteContent: WebsiteBuilderContent[], websiteStyle: WebsiteBuilderStyle }) => Promise<{ websiteContent: WebsiteBuilderContent[], websiteStyle: WebsiteBuilderStyle }>;

    getTemplate: (templateProperties: {
        templateType: WebsiteBuilderTemplateType;
        selectedWebsiteUrl?: string;
        selectedMusic?: RecordDetails;
        [key: string]: any;
    }) => WebsiteBuilderTemplate | null;
}

export interface WebsiteBuilder_collectDataExists extends WebsiteBuilderStore {
    websiteContent: WebsiteBuilderContent_collectDataExists[];
}

export interface WebsiteBuilder_dividerExists extends WebsiteBuilderStore {
    websiteContent: WebsiteBuilderContent_dividerExists[];
}

export interface WebsiteBuilder_externalLinksExists extends WebsiteBuilderStore {
    websiteContent: WebsiteBuilderContent_externalLinksExists[];
}

export interface WebsiteBuilder_imageExists extends WebsiteBuilderStore {
    websiteContent: WebsiteBuilderContent_imageExists[];
}

export interface WebsiteBuilder_musicExists extends WebsiteBuilderStore {
    websiteContent: WebsiteBuilderContent_musicExists[];
}

export interface WebsiteBuilder_paymentExists extends WebsiteBuilderStore {
    websiteContent: WebsiteBuilderContent_paymentExists[];
}

export interface WebsiteBuilder_presaveExists extends WebsiteBuilderStore {
    websiteContent: WebsiteBuilderContent_presaveExists[];
}

export interface WebsiteBuilder_socialPlatformsExists extends WebsiteBuilderStore {
    websiteContent: WebsiteBuilderContent_socialPlatformsExists[];
}

export interface WebsiteBuilder_streamingPlatformsExists extends WebsiteBuilderStore {
    websiteContent: WebsiteBuilderContent_streamingPlatformsExists[];
}

export interface WebsiteBuilder_tabsExists extends WebsiteBuilderStore {
    websiteContent: WebsiteBuilderContent_tabsExists[];
}

export interface WebsiteBuilder_textExists extends WebsiteBuilderStore {
    websiteContent: WebsiteBuilderContent_textExists[];
}

export interface WebsiteBuilder_tourExists extends WebsiteBuilderStore {
    websiteContent: WebsiteBuilderContent_tourExists[];
}

export interface WebsiteBuilder_videosExists extends WebsiteBuilderStore {
    websiteContent: WebsiteBuilderContent_videosExists[];
}

const useWebsiteBuilder = create(
    immer<WebsiteBuilderStore>((set, get) => ({
        // State
        websites: [],
        websitesGallery: [],
        websiteBuilderData: null,
        websiteContent: [],
        websiteStyle: {
            backgroundColor: DEFAULT_COLORS.backgroundColor,
            secondaryColor: DEFAULT_COLORS.secondaryColor,
            textColor: DEFAULT_COLORS.textColor,
            fontFamily: "DIN",
            textAlign: WebsiteTextAlign.CENTER,
            isSymphonyLogoEnabled: true,
            symphonyLogoStyle: SymphonyLogoStyleKey.BANNER,
        },
        isLoadingWebsiteBuilder: false,
        isWebsitesInitialized: false,
        isPublishingWebsite: false,
        isLoadingWebsitesGallery: false,
        websiteSlugText: '',
        showCreateNewWebsiteModal: false,
        showCreateNewWebsiteModalV2: false,
        initialWebsiteType: null,
        initialRecord: null,
        selectedWebsiteTemplate: null,
        selectedTemplateCategory: WEBSITE_TEMPLATE_CATEGORIES[0].name,

        // Actions
        setWebsites: (websites: WebsiteBuilderData[]) => set({ websites: websites }),
        setWebsitesGallery: (websites: WebsiteBuilderData[]) => set({ websitesGallery: websites }),
        setWebsiteContent: (content: WebsiteBuilderContent[]) => set({ websiteContent: content }),
        setWebsiteStyle: (style: WebsiteBuilderStyle) => set({ websiteStyle: style }),
        setIsLoadingWebsiteBuilder: (isLoading: boolean) => set({ isLoadingWebsiteBuilder: isLoading }),
        setIsPublishingWebsite: (isPublishing: boolean) => set({ isPublishingWebsite: isPublishing }),
        setIsLoadingWebsitesGallery: (isLoading: boolean) => set({ isLoadingWebsitesGallery: isLoading }),
        setWebsiteSlugText: (slug: string) => set({ websiteSlugText: slug }),
        setShowCreateNewWebsiteModal: (isShow: boolean) => set({ showCreateNewWebsiteModal: isShow }),
        setShowCreateNewWebsiteModalV2: (isShow: boolean) => set({ showCreateNewWebsiteModalV2: isShow }),
        setInitialWebsiteType: (websiteType: WebsiteType | null) => set({ initialWebsiteType: websiteType }),
        setInitialRecord: (record: RecordDetails | null) => set({ initialRecord: record }),
        setSelectedWebsiteTemplate: (template: WebsiteBuilderTemplateType) => set({ selectedWebsiteTemplate: template }),
        setSelectedTemplateCategory: (templateCategory: string) => set({ selectedTemplateCategory: templateCategory }),

        needsPaidBrand: () => {
            const { websiteContent } = get();
            const { isPaidBrand } = useCurrentBrand.getState() as BrandExistsState;

            if (isPaidBrand) {
                return false;
            }

            // Check for premium features in content
            let needsPaidBrandLocal = false;

            for (const content of websiteContent) {
                switch (content.type) {
                    case WebsiteBuilderContentType.MUSIC:
                        const musicStyle = content.musicContent?.style;
                        if (
                            musicStyle === MusicContentStyle.RECORD_PLAYER ||
                            musicStyle === MusicContentStyle.FULL_SCREEN
                        ) {
                            needsPaidBrandLocal = true;
                        }
                        break;
                    case WebsiteBuilderContentType.VIDEO:
                        const videoStyle = content.videoContent?.style;
                        if (
                            videoStyle === VideoContentStyle.FULL_SCREEN ||
                            videoStyle === VideoContentStyle.ROUNDED
                        ) {
                            needsPaidBrandLocal = true;
                        }
                        break;
                    case WebsiteBuilderContentType.IMAGE:
                        const imageStyle = content.imageContent?.style;

                        if (
                            imageStyle === ImageContentStyle.FULL_SCREEN ||
                            imageStyle === ImageContentStyle.CIRCLE ||
                            imageStyle === ImageContentStyle.ROUNDED
                        ) {
                            needsPaidBrandLocal = true;
                        }
                        break;
                    case WebsiteBuilderContentType.EXTERNAL_LINKS:
                        const externalLinksStyle = content.externalLinksContent?.style;
                        if (
                            externalLinksStyle === ExternalLinksStyle.HYPERLINKS ||
                            externalLinksStyle === ExternalLinksStyle.BOXES
                        ) {
                            needsPaidBrandLocal = true;
                        }
                        break;
                    case WebsiteBuilderContentType.STREAMING_PLATFORMS:
                        const streamingPlatformsStyle = content.streamingPlatformsContent?.style;
                        if (
                            streamingPlatformsStyle === StreamingPlatformsStyle.STACKED_BOXES ||
                            streamingPlatformsStyle === StreamingPlatformsStyle.ICONS
                        ) {
                            needsPaidBrandLocal = true;
                        }
                        break;
                }

                const {
                    isContentBackgroundColorEnabled,
                    isContentSecondaryColorEnabled,
                    isContentTextColorEnabled,
                    isContentTextSecondaryColorEnabled
                } = content;

                if(isContentBackgroundColorEnabled || isContentSecondaryColorEnabled || isContentTextColorEnabled || isContentTextSecondaryColorEnabled) {
                    needsPaidBrandLocal = true;
                }

                if (needsPaidBrandLocal) {
                    break;
                }
            }

            return needsPaidBrandLocal;
        },

        canSaveOrPublishWebsite: () => {
            const { websiteContent } = get();

            // Check if any content exists in the website
            if (!websiteContent || websiteContent.length === 0) {
                return false;
            }

            return true
        },

        getInitialWebsiteStyle: async (image?: string) => {
            const { currentBrand } = useCurrentBrand.getState() as BrandExistsState;
            const palette = await getPalette(image || currentBrand.image || '');
            set({
                websiteStyle: {
                    backgroundColor: palette.vibrant || Colors.purple,
                    secondaryColor: palette.lightVibrant || Colors.purpleLight,
                    textColor: '#FFFFFF',
                    fontFamily: "DIN",
                    textAlign: WebsiteTextAlign.CENTER,
                    isSymphonyLogoEnabled: true,
                    symphonyLogoStyle: SymphonyLogoStyleKey.BANNER,
                }
            })
        },

        resetWebsiteBuilder: () => set({
            websiteBuilderData: null,
            websiteContent: [],
            websiteSlugText: '',
            websiteStyle: {
                backgroundColor: DEFAULT_COLORS.backgroundColor,
                secondaryColor: DEFAULT_COLORS.secondaryColor,
                textColor: DEFAULT_COLORS.textColor,
                fontFamily: "DIN",
                textAlign: WebsiteTextAlign.CENTER,
                isSymphonyLogoEnabled: true,
                symphonyLogoStyle: SymphonyLogoStyleKey.BANNER,
            },
        }),

        isPublished: () => get().websiteBuilderData?.status === WebsiteStatus.PUBLISHED,

        cloneWebsite: async ({
            websiteContent: websiteContentProp,
            websiteStyle: websiteStyleProp
        }: {
            websiteContent: WebsiteBuilderContent[],
            websiteStyle: WebsiteBuilderStyle
        }): Promise<{ websiteContent: WebsiteBuilderContent[], websiteStyle: WebsiteBuilderStyle }> => {
            const {
                getSocialPlatforms,
                initialRecord
            } = get();
            const {
                currentBrand,
                getBrandImage,
                recentReleases
            } = useCurrentBrand.getState() as BrandExistsState;
            // Clone the website style
            const brandImage = getBrandImage();
            const paletteData = await getPalette(brandImage);
            const {
                backgroundColor,
                secondaryColor,
                textColor
            } = calculateThemeColors(paletteData);

            const websiteStyle = {
                ...websiteStyleProp,
                backgroundColor,
                secondaryColor,
                textColor,
            };

            const recordDetail = initialRecord;

            const isTabsContentExists = websiteContentProp.some(content => content.type === WebsiteBuilderContentType.TABS);

            // Clone the website content by mapping through each content item
            const websiteContent = websiteContentProp.map((content, index) => {
                const contentType = content.type;
                let newContent: Partial<WebsiteBuilderContent> = {
                    id: `${contentType}_${Date.now()}`,
                    type: contentType,
                    tabId: isTabsContentExists ? content.tabId : undefined,
                    contentIndex: index,
                };

                // Create a deep clone of each content item based on its type
                switch (contentType) {
                    case WebsiteBuilderContentType.COLLECT_DATA:
                        newContent.collectDataContent = {
                            title: `Collect Data ${Date.now()}`,
                            buttonText: 'Subscribe',
                            isBothEmailPhone: false,
                            isShowTitles: true,
                            postSubscribeMessage: 'Thank you for subscribing!',
                            info: [
                                {
                                    title: 'Email / Phone',
                                    placeholder: 'Enter your email / phone',
                                    type: CollectDataInputType.EMAIL_PHONE,
                                    isRequired: true,
                                },
                                {
                                    title: 'Name',
                                    placeholder: 'Enter your name',
                                    type: CollectDataInputType.TEXT,
                                    isRequired: false,
                                },
                            ],
                        };
                        break;
                    case WebsiteBuilderContentType.DIVIDER:
                        newContent.dividerContent = {
                            title: `Divider ${Date.now()}`,
                            style: DividerStyle.SOLID,
                        };
                        break;
                    case WebsiteBuilderContentType.EXTERNAL_LINKS:
                        newContent.externalLinksContent = {
                            title: `External Links ${Date.now()}`,
                            style: ExternalLinksStyle.STACKED_ROUNDED,
                            platforms: [{
                                title: 'Platform 1',
                                description: 'Description 1',
                                name: 'Platform 1',
                                icon: 'link',
                                link: 'https://platform1.com',
                                isEnabled: true,
                                placeholder: 'Enter link',
                            }],
                        };
                        break;
                    case WebsiteBuilderContentType.IMAGE:
                        newContent.imageContent = {
                            image: brandImage,
                            style: content.imageContent?.style || ImageContentStyle.SQUARE,
                            isTitle: content.imageContent?.isTitle || false,
                            title: content.imageContent?.title ? "Listen to" : '',
                            isSubtitle: content.imageContent?.isSubtitle || false,
                            subtitle: content.imageContent?.subtitle || 'My subtitle',
                        };
                        break;
                    case WebsiteBuilderContentType.MUSIC:
                        newContent.musicContent = {
                            title: `Music ${Date.now()}`,
                            style: content.musicContent?.style || MusicContentStyle.ARTWORK,
                            selectedMusic: recordDetail || undefined,
                        };
                        break;
                    case WebsiteBuilderContentType.PAYMENT:
                        newContent.paymentContent = {
                            title: `Payment ${Date.now()}`,
                        };
                        break;
                    case WebsiteBuilderContentType.PRESAVE:
                        newContent.presaveContent = {
                            title: `Presave ${Date.now()}`,
                            style: PresaveWebsiteBuilderStyle.BOXES,
                            hideReleaseDate: false,
                            presave: {
                                boosts: {
                                    auto_presaves: false,
                                    boost_follows: false,
                                    collect_emails: false,
                                },
                                platforms: [
                                    {
                                        name: PresavePlatform.SPOTIFY,
                                        isEnabled: true,
                                    },
                                    {
                                        name: PresavePlatform.APPLE_MUSIC,
                                        isEnabled: true,
                                    },
                                    {
                                        name: PresavePlatform.DEEZER,
                                        isEnabled: false,
                                    },
                                    {
                                        name: PresavePlatform.SOUNDCLOUD,
                                        isEnabled: false,
                                    },
                                    {
                                        name: PresavePlatform.AUDIOMACK,
                                        isEnabled: false,
                                    },
                                ],
                                redirect_url: 'https://example.com',
                                redirect_to_custom_webpage: false,
                                release_date: dayjs().format('YYYY-MM-DD'),
                                timezone: 'America/New_York',
                            }
                        };
                        break;
                    case WebsiteBuilderContentType.SOCIAL_PLATFORMS:
                        newContent.socialPlatformsContent = {
                            title: `Social Platforms ${Date.now()}`,
                            platforms: getSocialPlatforms(),
                        };
                        break;
                    case WebsiteBuilderContentType.STREAMING_PLATFORMS:
                        newContent.streamingPlatformsContent = {
                            title: `Streaming Platforms ${Date.now()}`,
                            platforms: [],
                            style: content.streamingPlatformsContent?.style || StreamingPlatformsStyle.BOXES,
                        };
                        break;
                    case WebsiteBuilderContentType.TABS:
                        newContent.tabsContent = {
                            title: `Tabs ${Date.now()}`,
                            activeTab: content.tabsContent?.tabs[0]?.id || 'tab1',
                            tabs: content.tabsContent?.tabs.map(tab => ({ ...tab })) || [],
                        };
                        break;
                    case WebsiteBuilderContentType.TEXT:
                        newContent.textContent = {
                            title: 'Title',
                            isSubtitle: content.textContent?.isSubtitle || false,
                            subtitle: content.textContent?.isSubtitle ? 'New subtitle' : '',
                        };
                        break;
                    case WebsiteBuilderContentType.TOUR:
                        newContent.tourContent = {
                            title: `Tour ${Date.now()}`,
                            tourDates: [],
                            hidePastTourDates: false,
                            style: content.tourContent?.style || TourStyle.BOXES,
                        };
                        break;
                    case WebsiteBuilderContentType.VIDEO:
                        newContent.videoContent = {
                            title: `Video ${Date.now()}`,
                            video: '',
                            thumbnail: '',
                            style: content.videoContent?.style || VideoContentStyle.SQUARE,
                        };
                        break;
                }

                return newContent as WebsiteBuilderContent;
            });

            set({
                websiteContent,
                websiteStyle,
            })

            return {
                websiteContent,
                websiteStyle,
            }
        },

        // Methods
        getWebsite: async ({
            websiteId,
            websiteSlug,
            brandSlug
        }: {
            websiteId?: number;
            websiteSlug?: string;
            brandSlug?: string
        }) => {
            set({ isLoadingWebsiteBuilder: true });
            try {
                const res = await getWebsite_api({ websiteId, websiteSlug, brandSlug });
                set({
                    websiteBuilderData: res,
                    websiteContent: res.websiteContent,
                    websiteStyle: res.websiteStyle,
                    websiteSlugText: res.slug,
                });
                return res;
            } catch (error) {
                console.error(error);
            } finally {
                set({ isLoadingWebsiteBuilder: false });
            }
        },
        getWebsites: async ({
            brandId: brandIdParam,
            isAllWebsites = false,
            includeBrand = false,
            resetInitializeWebsites = false,
        }: {
            brandId?: number,
            isAllWebsites?: boolean,
            includeBrand?: boolean,
            resetInitializeWebsites?: boolean,
        }) => {
            const { currentBrand } = useCurrentBrand.getState();
            const brandId = brandIdParam ?? currentBrand?.id ?? 0;

            set({
                isLoadingWebsiteBuilder: true,
                isWebsitesInitialized: resetInitializeWebsites,
            });
            try {
                const res = await getWebsites_api({ brandId, isAllWebsites, includeBrand });
                set({ websites: res });
                return res;
            } catch (error) {
                console.error(error);
                return [];
            } finally {
                set({
                    isLoadingWebsiteBuilder: false,
                    isWebsitesInitialized: true,
                });
            }
        },
        createWebsite: async () => {
            const {
                websiteSlugText,
                websiteContent,
                websiteStyle,
                selectedWebsiteTemplate: selectedTemplate
            } = get();
            const { currentBrand } = useCurrentBrand.getState();
            const brandId = currentBrand?.id ?? 0;

            if (!websiteSlugText || !websiteContent || !brandId) {
                throw new Error('Website slug and content are required');
            }

            const websiteBuilderPayload: WebsiteBuilderDataPayload = {
                brandId: brandId,
                slug: websiteSlugText,
                status: WebsiteStatus.PUBLISHED,
                websiteContent: websiteContent,
                websiteStyle: websiteStyle,
            };

            try {
                set({ isPublishingWebsite: true });
                const res = await createWebsite_api(websiteBuilderPayload);
                track('Published "New Website"', {
                    type: "Website Builder",
                    template: selectedTemplate,
                })
                set({
                    websiteBuilderData: res,
                    websiteContent: res.websiteContent,
                    websiteStyle: res.websiteStyle,
                    websiteSlugText: res.slug,
                });
                return res;
            } catch (error) {
                console.error(error);
                return null;
            } finally {
                set({ isPublishingWebsite: false });
            }
        },
        updateWebsite: async (options?: {
            websiteId?: number;
            websiteBuilderPayload?: WebsiteBuilderDataPayload;
        }) => {
            const { websiteBuilderData, websiteContent, websiteStyle, websiteSlugText } = get();
            const { currentBrand } = useCurrentBrand.getState();

            const websiteId = options?.websiteId ?? websiteBuilderData?.id ?? 0;
            let websiteBuilderPayload = options?.websiteBuilderPayload ?? null;
            const brandId = currentBrand?.id ?? 0;

            if (!websiteId) {
                return;
            }

            try {
                set({ isPublishingWebsite: true });
                if (!websiteBuilderPayload) {
                    websiteBuilderPayload = {
                        brandId,
                        slug: websiteSlugText,
                        status: WebsiteStatus.PUBLISHED,
                        websiteContent: websiteContent,
                        websiteStyle: websiteStyle,
                    };
                }

                const res = await updateWebsite_api(websiteId, websiteBuilderPayload);
                return res;
            } catch (error) {
                console.error(error);
            } finally {
                set({ isPublishingWebsite: false });
            }
        },
        deleteWebsite: async (websiteId: number) => {
            try {
                set({ isLoadingWebsiteBuilder: true });
                const res = await deleteWebsite_api(websiteId);
                return res;
            } catch (error) {
                console.error(error);
                return null;
            } finally {
                set({ isLoadingWebsiteBuilder: false });
            }
        },

        getSocialPlatforms: () => {
            const { currentUser } = useCurrentUser.getState();
            const { currentBrand } = useCurrentBrand.getState();
            const brandId = currentBrand?.id ?? 0;

            if (!brandId) {
                return [];
            }

            const facebookUrlDefault = 'https://facebook.com/';
            const instagramUrlDefault = 'https://instagram.com/';
            const tiktokUrlDefault = 'https://tiktok.com/@';
            const xUrlDefault = 'https://x.com/';
            const youtubeUrlDefault = 'https://youtube.com/c/';

            const facebookUsername = currentBrand?.connections.facebook_page?.name || '';
            const instagramUsername = currentBrand?.connections.instagram_page?.instagramAccount?.username || currentBrand?.connections.business_discovery_instagram?.username || '';
            const youtubeChannelId = currentBrand?.connections.youtube_channel?.id?.channelId || currentBrand?.connections.youtube_channel?.id || '';

            const facebookUrl = `${facebookUrlDefault}${facebookUsername}`;
            const instagramUrl = `${instagramUrlDefault}${instagramUsername}`;
            const tiktokUrl = currentBrand?.connections.tiktok?.url || tiktokUrlDefault;
            const xUrl = convertTwitterUrlToXUrl(currentBrand?.connections.twitter?.url || '') || xUrlDefault;
            const youtubeUrl = `${youtubeUrlDefault}${youtubeChannelId}`;

            return [
                {
                    title: 'Facebook',
                    description: 'Follow us on Facebook',
                    placeholder: facebookUrlDefault,
                    name: SocialPlatform.FACEBOOK,
                    icon: 'facebook',
                    link: facebookUrl,
                    isEnabled: true,
                },
                {
                    title: 'Instagram',
                    description: 'Follow us on Instagram',
                    placeholder: instagramUrlDefault,
                    name: SocialPlatform.INSTAGRAM,
                    icon: 'instagram',
                    link: instagramUrl,
                    isEnabled: true,
                },
                {
                    title: 'TikTok',
                    description: 'Follow us on TikTok',
                    placeholder: tiktokUrlDefault,
                    name: SocialPlatform.TIKTOK,
                    icon: 'tiktok',
                    link: tiktokUrl,
                    isEnabled: true,
                },
                {
                    title: 'X',
                    description: 'Follow us on X',
                    placeholder: xUrlDefault,
                    name: SocialPlatform.X,
                    icon: 'x',
                    link: xUrl,
                    isEnabled: true,
                },
                {
                    title: 'YouTube',
                    description: 'Subscribe to our YouTube channel',
                    placeholder: youtubeUrlDefault,
                    name: SocialPlatform.YOUTUBE,
                    icon: 'youtube',
                    link: youtubeUrl,
                    isEnabled: true,
                },
                {
                    title: 'Email',
                    description: 'Email us',
                    placeholder: 'Enter your email',
                    name: SocialPlatform.EMAIL,
                    icon: 'email',
                    link: `mailto:${currentUser?.email}`,
                    isEnabled: true,
                }
            ] as PlatformItem[]
        },

        getWebsitesGallery: async ({ websiteIds }: { websiteIds?: number[] }): Promise<WebsiteBuilderData[]> => {
            set({ isLoadingWebsitesGallery: true });
            try {
                const res = await getWebsitesGallery_api({ websiteIds });
                set({ websitesGallery: res });
                return res;
            } catch (error) {
                console.error(error);
                return [];
            } finally {
                set({ isLoadingWebsitesGallery: false });
            }
        },

        getTemplate: (templateProperties: {
            templateType: WebsiteBuilderTemplateType;
            websiteStyleProp?: WebsiteBuilderStyle;
            recordDetails?: RecordDetails;
            [key: string]: any;
        }): WebsiteBuilderTemplate | null => {
            const { websiteStyle, initialRecord, getSocialPlatforms } = get();
            const { currentBrand, getBrandImage } = useCurrentBrand.getState();
            const {
                templateType,
                websiteStyleProp,
                recordDetails: recordDetailsProp
            } = templateProperties;

            const recordDetails = recordDetailsProp || initialRecord;
            const platformsLocal = getStreamingLinksFromRecord(recordDetails);
            const selectedMusicTitle = recordDetails?.name || '';
            const selectedMusicArtist = recordDetails?.primary_artist || '';

            const brandName = currentBrand?.name ?? '';
            const brandImage = getBrandImage();

            let websiteContentLocal: WebsiteBuilderContent[] = [];
            let websiteStyleLocal: WebsiteBuilderStyle = websiteStyleProp || websiteStyle
            const socialPlatforms = getSocialPlatforms();
            const hasMusicPreviewLocal = hasMusicPreview(recordDetails);
            const firstMusicContent = hasMusicPreviewLocal ?
                {
                    type: WebsiteBuilderContentType.MUSIC,
                    musicContent: {
                        title: 'Music',
                        style: MusicContentStyle.ARTWORK,
                        selectedMusic: recordDetails,
                    } as MusicContentType
                }
                :
                {
                    type: WebsiteBuilderContentType.IMAGE,
                    imageContent: {
                        title: `Image ${Date.now()}`,
                        image: recordDetails?.thumbnail_url || brandImage,
                        style: ImageContentStyle.SQUARE,
                    } as ImageContentType
                };

            const templateInfo = WEBSITE_TEMPLATE_INFO[templateType];
            const templateTitle = templateInfo.title;

            switch (templateType) {
                case WebsiteBuilderTemplateType.BLANK:
                    return {
                        title: templateTitle,
                        type: templateType,
                        websiteContent: [],
                        websiteStyle: websiteStyleLocal
                    }
                case WebsiteBuilderTemplateType.BASIC:
                    websiteContentLocal = [
                        {
                            type: WebsiteBuilderContentType.IMAGE,
                            imageContent: {
                                title: `Image ${Date.now()}`,
                                image: brandImage,
                                style: ImageContentStyle.SMALL_CIRCLE,
                            } as ImageContentType
                        },
                        {
                            type: WebsiteBuilderContentType.SOCIAL_PLATFORMS,
                            socialPlatformsContent: {
                                title: `Social Platforms ${Date.now()}`,
                                platforms: socialPlatforms
                            } as SocialPlatformsContentType
                        },
                        {
                            type: WebsiteBuilderContentType.EXTERNAL_LINKS,
                            externalLinksContent: {
                                title: 'External Links',
                                style: ExternalLinksStyle.STACKED_ROUNDED,
                                platforms: [
                                    {
                                        title: 'Platform 1',
                                        description: 'Description 1',
                                        name: 'Platform 1',
                                        icon: 'link',
                                        link: 'https://platform1.com',
                                        isEnabled: true,
                                        placeholder: 'Enter link',
                                    },
                                    {
                                        title: 'Platform 2',
                                        description: 'Description 2',
                                        name: 'Platform 2',
                                        icon: 'link',
                                        link: 'https://platform2.com',
                                        isEnabled: true,
                                        placeholder: 'Enter link',
                                    },
                                    {
                                        title: 'Platform 3',
                                        description: 'Description 3',
                                        name: 'Platform 3',
                                        icon: 'link',
                                        link: 'https://platform3.com',
                                        isEnabled: true,
                                        placeholder: 'Enter link',
                                    }
                                ] as PlatformItem[],
                            } as ExternalLinksContentType
                        }
                    ] as WebsiteBuilderContent[]
                    return {
                        title: templateTitle,
                        type: templateType,
                        websiteContent: websiteContentLocal,
                        websiteStyle: websiteStyleLocal
                    }

                case WebsiteBuilderTemplateType.MUSIC_1:
                    websiteContentLocal = [
                        firstMusicContent,
                        {
                            type: WebsiteBuilderContentType.STREAMING_PLATFORMS,
                            streamingPlatformsContent: {
                                title: 'Streaming Platforms',
                                platforms: platformsLocal,
                                style: StreamingPlatformsStyle.BOXES,
                            } as StreamingPlatformsContentType
                        },
                        {
                            type: WebsiteBuilderContentType.SOCIAL_PLATFORMS,
                            socialPlatformsContent: {
                                title: 'Social Platforms',
                                platforms: socialPlatforms
                            } as SocialPlatformsContentType
                        }
                    ] as WebsiteBuilderContent[]

                    return {
                        title: templateTitle,
                        type: templateType,
                        websiteContent: websiteContentLocal,
                        websiteStyle: websiteStyleLocal
                    }
                case WebsiteBuilderTemplateType.MUSIC_2:
                    websiteContentLocal = [
                        hasMusicPreviewLocal ?
                            {
                                type: WebsiteBuilderContentType.MUSIC,
                                musicContent: {
                                    title: 'Music',
                                    style: MusicContentStyle.FULL_SCREEN,
                                    selectedMusic: recordDetails,
                                } as MusicContentType
                            }
                            :
                            {
                                type: WebsiteBuilderContentType.IMAGE,
                                imageContent: {
                                    title: `Image ${Date.now()}`,
                                    image: recordDetails?.thumbnail_url || brandImage,
                                    style: ImageContentStyle.FULL_SCREEN,
                                } as ImageContentType
                            },
                        {
                            type: WebsiteBuilderContentType.SOCIAL_PLATFORMS,
                            socialPlatformsContent: {
                                title: 'Social Platforms',
                                platforms: socialPlatforms
                            } as SocialPlatformsContentType
                        },
                        {
                            type: WebsiteBuilderContentType.STREAMING_PLATFORMS,
                            streamingPlatformsContent: {
                                title: 'Streaming Platforms',
                                platforms: platformsLocal,
                                style: StreamingPlatformsStyle.STACKED_BOXES,
                            } as StreamingPlatformsContentType
                        },
                        {
                            type: WebsiteBuilderContentType.COLLECT_DATA,
                            collectDataContent: {
                                title: 'Collect Data',
                                buttonText: 'Subscribe',
                                isBothEmailPhone: false,
                                isShowTitles: true,
                                info: [
                                    {
                                        title: 'Email / Phone',
                                        placeholder: 'Enter your email / phone',
                                        type: CollectDataInputType.EMAIL_PHONE,
                                        isRequired: true,
                                    },
                                    {
                                        title: 'Name',
                                        placeholder: 'Enter your name',
                                        type: CollectDataInputType.TEXT,
                                        isRequired: false,
                                    }
                                ]
                            } as CollectDataContentType
                        }
                    ] as WebsiteBuilderContent[]

                    return {
                        title: templateTitle,
                        type: templateType,
                        websiteContent: websiteContentLocal,
                        websiteStyle: websiteStyleLocal
                    }
                case WebsiteBuilderTemplateType.MUSIC_3:
                    const smallCircleImageContent = {
                        type: WebsiteBuilderContentType.IMAGE,
                        imageContent: {
                            title: `Image ${Date.now()}`,
                            image: recordDetails?.thumbnail_url || brandImage,
                            style: ImageContentStyle.CIRCLE,
                        } as ImageContentType
                    }
                    websiteContentLocal = [
                        ...(hasMusicPreviewLocal ? [
                            smallCircleImageContent,
                            {
                                type: WebsiteBuilderContentType.MUSIC,
                                musicContent: {
                                    title: 'Music',
                                    style: MusicContentStyle.PLAYER,
                                    selectedMusic: recordDetails,
                                } as MusicContentType
                            }
                        ] : [smallCircleImageContent]),
                        {
                            type: WebsiteBuilderContentType.STREAMING_PLATFORMS,
                            streamingPlatformsContent: {
                                title: 'Streaming Platforms',
                                platforms: platformsLocal,
                                style: StreamingPlatformsStyle.TRADITIONAL,
                            } as StreamingPlatformsContentType
                        },
                        {
                            type: WebsiteBuilderContentType.SOCIAL_PLATFORMS,
                            socialPlatformsContent: {
                                title: 'Social Platforms',
                                platforms: socialPlatforms
                            } as SocialPlatformsContentType
                        }
                    ] as WebsiteBuilderContent[]

                    return {
                        title: templateTitle,
                        type: templateType,
                        websiteContent: websiteContentLocal,
                        websiteStyle: websiteStyleLocal
                    }
                case WebsiteBuilderTemplateType.MUSIC_4:
                    websiteContentLocal = [
                        firstMusicContent,
                        {
                            type: WebsiteBuilderContentType.TABS,
                            tabsContent: {
                                title: 'Tabs',
                                tabs: [
                                    {
                                        title: 'Music',
                                        id: 'music',
                                    },
                                    {
                                        title: 'About Me',
                                        id: 'about-me',
                                    },
                                ] as TabItem[],
                                activeTab: 'music',
                            } as TabContentType
                        },
                        {
                            type: WebsiteBuilderContentType.STREAMING_PLATFORMS,
                            tabId: 'music',
                            streamingPlatformsContent: {
                                title: 'Streaming Platforms',
                                platforms: platformsLocal,
                                style: StreamingPlatformsStyle.BOXES,
                            } as StreamingPlatformsContentType
                        },
                        {
                            type: WebsiteBuilderContentType.COLLECT_DATA,
                            tabId: 'music',
                            collectDataContent: {
                                title: 'Collect Data',
                                buttonText: 'Subscribe',
                                isBothEmailPhone: false,
                                isShowTitles: true,
                                info: [
                                    {
                                        title: 'Email / Phone',
                                        placeholder: 'Enter your email / phone',
                                        type: CollectDataInputType.EMAIL_PHONE,
                                        isRequired: true,
                                    },
                                    {
                                        title: 'Name',
                                        placeholder: 'Enter your name',
                                        type: CollectDataInputType.TEXT,
                                        isRequired: false,
                                    }
                                ]
                            } as CollectDataContentType
                        },
                        {
                            type: WebsiteBuilderContentType.IMAGE,
                            tabId: 'about-me',
                            imageContent: {
                                title: `Image ${Date.now()}`,
                                image: brandImage,
                                style: ImageContentStyle.SMALL_CIRCLE,
                            } as ImageContentType
                        },
                        {
                            type: WebsiteBuilderContentType.SOCIAL_PLATFORMS,
                            tabId: 'about-me',
                            socialPlatformsContent: {
                                title: 'Social Platforms',
                                platforms: socialPlatforms
                            } as SocialPlatformsContentType
                        },
                        {
                            type: WebsiteBuilderContentType.TEXT,
                            tabId: 'about-me',
                            textContent: {
                                title: 'Nice to meet you',
                                subtitle: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
                                isSubtitle: true,
                            } as TextContentType
                        }
                    ] as WebsiteBuilderContent[]

                    return {
                        title: templateTitle,
                        type: templateType,
                        websiteContent: websiteContentLocal,
                        websiteStyle: websiteStyleLocal
                    }

                case WebsiteBuilderTemplateType.COLLECT_DATA_1:
                    websiteContentLocal = [
                        {
                            type: WebsiteBuilderContentType.IMAGE,
                            imageContent: {
                                title: `Image ${Date.now()}`,
                                image: brandImage,
                                style: ImageContentStyle.SQUARE,
                            } as ImageContentType
                        },
                        {
                            type: WebsiteBuilderContentType.TEXT,
                            textContent: {
                                title: `${brandName} wants to connect with you!`,
                                isSubtitle: true,
                                subtitle: 'Fill out the form below to get started.',
                            } as TextContentType
                        },
                        {
                            type: WebsiteBuilderContentType.COLLECT_DATA,
                            collectDataContent: {
                                title: `Collect Data ${Date.now()}`,
                                buttonText: 'Subscribe',
                                isBothEmailPhone: false,
                                isShowTitles: true,
                                postSubscribeMessage: 'Thank you for subscribing!',
                                info: [
                                    {
                                        title: 'Email / Phone',
                                        placeholder: 'Enter your email / phone',
                                        type: CollectDataInputType.EMAIL_PHONE,
                                        isRequired: true,
                                    },
                                    {
                                        title: 'Name',
                                        placeholder: 'Enter your name',
                                        type: CollectDataInputType.TEXT,
                                        isRequired: false,
                                    },
                                ],
                            } as CollectDataContentType
                        }
                    ] as WebsiteBuilderContent[]

                    return {
                        title: templateTitle,
                        type: templateType,
                        websiteContent: websiteContentLocal,
                        websiteStyle: websiteStyleLocal
                    }
                case WebsiteBuilderTemplateType.COLLECT_DATA_2:
                    websiteContentLocal = [
                        {
                            type: WebsiteBuilderContentType.IMAGE,
                            imageContent: {
                                image: brandImage,
                                style: ImageContentStyle.FULL_SCREEN,
                                isTitle: true,
                                title: `${brandName} wants to connect with you!`,
                                isSubtitle: true,
                                subtitle: 'Fill out the form below to get started.',
                            } as ImageContentType
                        },
                        {
                            type: WebsiteBuilderContentType.SOCIAL_PLATFORMS,
                            socialPlatformsContent: {
                                title: 'Social Platforms',
                                platforms: socialPlatforms
                            } as SocialPlatformsContentType
                        },
                        {
                            type: WebsiteBuilderContentType.COLLECT_DATA,
                            collectDataContent: {
                                title: `Collect Data ${Date.now()}`,
                                buttonText: 'Subscribe',
                                isBothEmailPhone: false,
                                isShowTitles: true,
                                postSubscribeMessage: 'Thank you for subscribing!',
                                info: [
                                    {
                                        title: 'Email / Phone',
                                        placeholder: 'Enter your email / phone',
                                        type: CollectDataInputType.EMAIL_PHONE,
                                        isRequired: true,
                                    },
                                    {
                                        title: 'Name',
                                        placeholder: 'Enter your name',
                                        type: CollectDataInputType.TEXT,
                                        isRequired: false,
                                    },
                                    {
                                        title: 'City',
                                        placeholder: 'Enter your city',
                                        type: CollectDataInputType.TEXT,
                                        isRequired: false,
                                    },
                                    {
                                        title: 'Favorite Song',
                                        placeholder: 'Enter your favorite song',
                                        type: CollectDataInputType.TEXT,
                                        isRequired: false,
                                    }
                                ]
                            } as CollectDataContentType
                        },
                        {
                            type: WebsiteBuilderContentType.TEXT,
                            textContent: {
                                title: 'Thank you for subscribing!',
                                isSubtitle: true,
                                subtitle: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
                            } as TextContentType
                        }
                    ] as WebsiteBuilderContent[]

                    return {
                        title: templateTitle,
                        type: templateType,
                        websiteContent: websiteContentLocal,
                        websiteStyle: websiteStyleLocal
                    }
                case WebsiteBuilderTemplateType.COLLECT_DATA_3:
                    websiteContentLocal = [
                        {
                            type: WebsiteBuilderContentType.IMAGE,
                            imageContent: {
                                image: brandImage,
                                style: ImageContentStyle.CIRCLE,
                            } as ImageContentType
                        },
                        {
                            type: WebsiteBuilderContentType.SOCIAL_PLATFORMS,
                            socialPlatformsContent: {
                                title: 'Social Platforms',
                                platforms: socialPlatforms
                            } as SocialPlatformsContentType
                        },
                        {
                            type: WebsiteBuilderContentType.TABS,
                            tabsContent: {
                                title: 'Tabs',
                                tabs: [
                                    {
                                        title: 'Sign Up',
                                        id: 'sign-up',
                                    },
                                    {
                                        title: 'Music',
                                        id: 'music',
                                    },
                                    {
                                        title: 'About Me',
                                        id: 'about-me',
                                    },
                                ] as TabItem[],
                                activeTab: 'sign-up',
                            } as TabContentType
                        },
                        {
                            type: WebsiteBuilderContentType.COLLECT_DATA,
                            tabId: 'sign-up',
                            collectDataContent: {
                                title: `Collect Data ${Date.now()}`,
                                buttonText: 'Subscribe',
                                isBothEmailPhone: true,
                                isShowTitles: true,
                                postSubscribeMessage: 'Thank you for subscribing!',
                                info: [
                                    {
                                        title: 'Email / Phone',
                                        placeholder: 'Enter your email / phone',
                                        type: CollectDataInputType.EMAIL_PHONE,
                                        isRequired: true,
                                    },
                                    {
                                        title: 'Name',
                                        placeholder: 'Enter your name',
                                        type: CollectDataInputType.TEXT,
                                        isRequired: false,
                                    },
                                    {
                                        title: 'City',
                                        placeholder: 'Enter your city',
                                        type: CollectDataInputType.TEXT,
                                        isRequired: false,
                                    },

                                ]
                            } as CollectDataContentType
                        },
                        {
                            ...firstMusicContent,
                            tabId: 'music',
                        },
                        {
                            type: WebsiteBuilderContentType.STREAMING_PLATFORMS,
                            tabId: 'music',
                            streamingPlatformsContent: {
                                title: 'Streaming Platforms',
                                platforms: platformsLocal,
                                style: StreamingPlatformsStyle.BOXES,
                            } as StreamingPlatformsContentType
                        },
                        {
                            type: WebsiteBuilderContentType.IMAGE,
                            tabId: 'about-me',
                            imageContent: {
                                title: `Image ${Date.now()}`,
                                image: brandImage,
                                style: ImageContentStyle.SMALL_CIRCLE,
                            } as ImageContentType
                        },
                        {
                            type: WebsiteBuilderContentType.TEXT,
                            tabId: 'about-me',
                            textContent: {
                                title: `About ${brandName}`,
                                subtitle: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
                                isSubtitle: true,
                            } as TextContentType
                        },
                        {
                            type: WebsiteBuilderContentType.SOCIAL_PLATFORMS,
                            tabId: 'about-me',
                            socialPlatformsContent: {
                                title: 'Social Platforms',
                                platforms: socialPlatforms
                            } as SocialPlatformsContentType
                        }
                    ] as WebsiteBuilderContent[]

                    return {
                        title: templateTitle,
                        type: templateType,
                        websiteContent: websiteContentLocal,
                        websiteStyle: websiteStyleLocal
                    }
                case WebsiteBuilderTemplateType.COLLECT_DATA_4:
                    websiteContentLocal = [
                        {
                            type: WebsiteBuilderContentType.IMAGE,
                            imageContent: {
                                image: brandImage,
                                style: ImageContentStyle.ROUNDED,
                                isTitle: true,
                                title: `${brandName} wants to connect with you!`,
                                isSubtitle: true,
                                subtitle: 'Fill out the form below to get started.',
                            } as ImageContentType
                        },
                        {
                            type: WebsiteBuilderContentType.SOCIAL_PLATFORMS,
                            socialPlatformsContent: {
                                title: 'Social Platforms',
                                platforms: socialPlatforms
                            } as SocialPlatformsContentType
                        },
                        {
                            type: WebsiteBuilderContentType.STREAMING_PLATFORMS,
                            streamingPlatformsContent: {
                                title: 'Streaming Platforms',
                                platforms: platformsLocal,
                                style: StreamingPlatformsStyle.ICONS,
                            } as StreamingPlatformsContentType
                        },
                        {
                            type: WebsiteBuilderContentType.TEXT,
                            textContent: {
                                title: 'Tell me who you are!',
                                subtitle: 'Please fill out the form below to get started.',
                                isSubtitle: true,
                            } as TextContentType
                        },
                        {
                            type: WebsiteBuilderContentType.COLLECT_DATA,
                            collectDataContent: {
                                title: `Collect Data ${Date.now()}`,
                                buttonText: 'Subscribe',
                                isBothEmailPhone: true,
                                isShowTitles: true,
                                postSubscribeMessage: 'Thank you for subscribing!',
                                info: [
                                    {
                                        title: 'Email / Phone',
                                        placeholder: 'Enter your email / phone',
                                        type: CollectDataInputType.EMAIL_PHONE,
                                        isRequired: true,
                                    },
                                    {
                                        title: 'Name',
                                        placeholder: 'Enter your name',
                                        type: CollectDataInputType.TEXT,
                                        isRequired: false,
                                    },
                                    {
                                        title: 'City',
                                        placeholder: 'Enter your city',
                                        type: CollectDataInputType.TEXT,
                                        isRequired: false,
                                    }
                                ]
                            } as CollectDataContentType
                        }
                    ] as WebsiteBuilderContent[]

                    return {
                        title: templateTitle,
                        type: templateType,
                        websiteContent: websiteContentLocal,
                        websiteStyle: websiteStyleLocal
                    }

                case WebsiteBuilderTemplateType.TOUR_1:
                    websiteContentLocal = [
                        {
                            type: WebsiteBuilderContentType.IMAGE,
                            imageContent: {
                                title: `Image ${Date.now()}`,
                                image: brandImage,
                                style: ImageContentStyle.SQUARE,
                            } as ImageContentType
                        },
                        {
                            type: WebsiteBuilderContentType.TEXT,
                            textContent: {
                                title: `${brandName} Tour Dates`,
                                subtitle: 'See when we\'re coming to a city near you.',
                                isSubtitle: true,
                            } as TextContentType
                        },
                        {
                            type: WebsiteBuilderContentType.TOUR,
                            tourContent: {
                                title: `Tour ${Date.now()}`,
                                tourDates: [
                                    {
                                        date: dayjs().add(7, 'day').format('YYYY-MM-DD'),
                                        city: 'New York',
                                        country: 'United States',
                                        venue: 'Madison Square Garden',
                                        ticketUrl: 'https://www.ticketmaster.com/event/1234567890',
                                    },
                                    {
                                        date: dayjs().add(14, 'day').format('YYYY-MM-DD'),
                                        city: 'Los Angeles',
                                        country: 'United States',
                                        venue: 'Staples Center',
                                        ticketUrl: 'https://www.ticketmaster.com/event/1234567890',
                                    },
                                    {
                                        date: dayjs().add(21, 'day').format('YYYY-MM-DD'),
                                        city: 'Chicago',
                                        country: 'United States',
                                        venue: 'United Center',
                                        ticketUrl: 'https://www.ticketmaster.com/event/1234567890',
                                    }
                                ] as TourDate[],
                                hidePastTourDates: false,
                                style: TourStyle.BOXES,
                            } as TourContentType
                        }
                    ] as WebsiteBuilderContent[]

                    return {
                        title: templateTitle,
                        type: templateType,
                        websiteContent: websiteContentLocal,
                        websiteStyle: websiteStyleLocal
                    }
                case WebsiteBuilderTemplateType.TOUR_2:
                    websiteContentLocal = [
                        {
                            type: WebsiteBuilderContentType.IMAGE,
                            imageContent: {
                                image: brandImage,
                                style: ImageContentStyle.FULL_SCREEN,
                                isTitle: true,
                                title: `${brandName} On Tour`,
                                isSubtitle: true,
                                subtitle: 'Get your tickets before they sell out!',
                            } as ImageContentType
                        },
                        {
                            type: WebsiteBuilderContentType.SOCIAL_PLATFORMS,
                            socialPlatformsContent: {
                                title: 'Social Platforms',
                                platforms: socialPlatforms
                            } as SocialPlatformsContentType
                        },
                        {
                            type: WebsiteBuilderContentType.TOUR,
                            tourContent: {
                                title: `Tour ${Date.now()}`,
                                tourDates: [
                                    {
                                        date: dayjs().add(7, 'day').format('YYYY-MM-DD'),
                                        city: 'New York',
                                        country: 'United States',
                                        venue: 'Madison Square Garden',
                                        ticketUrl: 'https://www.ticketmaster.com/event/1234567890',
                                    },
                                    {
                                        date: dayjs().add(14, 'day').format('YYYY-MM-DD'),
                                        city: 'Los Angeles',
                                        country: 'United States',
                                        venue: 'Staples Center',
                                        ticketUrl: 'https://www.ticketmaster.com/event/1234567890',
                                    },
                                    {
                                        date: dayjs().add(21, 'day').format('YYYY-MM-DD'),
                                        city: 'Chicago',
                                        country: 'United States',
                                        venue: 'United Center',
                                        ticketUrl: 'https://www.ticketmaster.com/event/1234567890',
                                    }
                                ] as TourDate[],
                                hidePastTourDates: true,
                                style: TourStyle.STACKED,
                            } as TourContentType
                        },
                        {
                            type: WebsiteBuilderContentType.TEXT,
                            textContent: {
                                title: 'VIP Packages Available',
                                isSubtitle: true,
                                subtitle: 'Contact us for special VIP packages including meet & greets, sound check access, and exclusive merchandise.',
                            } as TextContentType
                        }
                    ] as WebsiteBuilderContent[]

                    return {
                        title: templateTitle,
                        type: templateType,
                        websiteContent: websiteContentLocal,
                        websiteStyle: websiteStyleLocal
                    }
                case WebsiteBuilderTemplateType.TOUR_3:
                    websiteContentLocal = [
                        {
                            type: WebsiteBuilderContentType.IMAGE,
                            imageContent: {
                                image: brandImage,
                                style: ImageContentStyle.CIRCLE,
                            } as ImageContentType
                        },
                        {
                            type: WebsiteBuilderContentType.SOCIAL_PLATFORMS,
                            socialPlatformsContent: {
                                title: 'Social Platforms',
                                platforms: socialPlatforms
                            } as SocialPlatformsContentType
                        },
                        {
                            type: WebsiteBuilderContentType.TABS,
                            tabsContent: {
                                title: 'Tabs',
                                tabs: [
                                    {
                                        title: 'Tour Dates',
                                        id: 'tour-dates',
                                    },
                                    {
                                        title: 'Music',
                                        id: 'music',
                                    },
                                    {
                                        title: 'About',
                                        id: 'about',
                                    },
                                ] as TabItem[],
                                activeTab: 'tour-dates',
                            } as TabContentType
                        },
                        {
                            type: WebsiteBuilderContentType.TEXT,
                            tabId: 'tour-dates',
                            textContent: {
                                title: `${brandName} World Tour`,
                                subtitle: 'Coming to a city near you. Get your tickets now!',
                                isSubtitle: true,
                            } as TextContentType
                        },
                        {
                            type: WebsiteBuilderContentType.TOUR,
                            tabId: 'tour-dates',
                            tourContent: {
                                title: `Tour ${Date.now()}`,
                                tourDates: [
                                    {
                                        date: dayjs().add(7, 'day').format('YYYY-MM-DD'),
                                        city: 'New York',
                                        country: 'United States',
                                        venue: 'Madison Square Garden',
                                        ticketUrl: 'https://www.ticketmaster.com/event/1234567890',
                                    },
                                    {
                                        date: dayjs().add(14, 'day').format('YYYY-MM-DD'),
                                        city: 'Los Angeles',
                                        country: 'United States',
                                        venue: 'Staples Center',
                                        ticketUrl: 'https://www.ticketmaster.com/event/1234567890',
                                    },
                                    {
                                        date: dayjs().add(21, 'day').format('YYYY-MM-DD'),
                                        city: 'Chicago',
                                        country: 'United States',
                                        venue: 'United Center',
                                        ticketUrl: 'https://www.ticketmaster.com/event/1234567890',
                                    }
                                ] as TourDate[],
                                hidePastTourDates: true,
                                style: TourStyle.BOXES,
                            } as TourContentType
                        },
                        {
                            ...firstMusicContent,
                            tabId: 'music',
                        },
                        {
                            type: WebsiteBuilderContentType.STREAMING_PLATFORMS,
                            tabId: 'music',
                            streamingPlatformsContent: {
                                title: 'Streaming Platforms',
                                platforms: platformsLocal,
                                style: StreamingPlatformsStyle.BOXES,
                            } as StreamingPlatformsContentType
                        },
                        {
                            type: WebsiteBuilderContentType.IMAGE,
                            tabId: 'about',
                            imageContent: {
                                title: `Image ${Date.now()}`,
                                image: brandImage,
                                style: ImageContentStyle.SMALL_CIRCLE,
                            } as ImageContentType
                        },
                        {
                            type: WebsiteBuilderContentType.TEXT,
                            tabId: 'about',
                            textContent: {
                                title: `About ${brandName}`,
                                subtitle: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
                                isSubtitle: true,
                            } as TextContentType
                        }
                    ] as WebsiteBuilderContent[]

                    return {
                        title: templateTitle,
                        type: templateType,
                        websiteContent: websiteContentLocal,
                        websiteStyle: websiteStyleLocal
                    }
                case WebsiteBuilderTemplateType.TOUR_4:
                    websiteContentLocal = [
                        {
                            type: WebsiteBuilderContentType.IMAGE,
                            imageContent: {
                                image: brandImage,
                                style: ImageContentStyle.ROUNDED,
                                isTitle: true,
                                title: `${brandName} Live In Concert`,
                                isSubtitle: true,
                                subtitle: 'Experience the music like never before',
                            } as ImageContentType
                        },
                        {
                            type: WebsiteBuilderContentType.SOCIAL_PLATFORMS,
                            socialPlatformsContent: {
                                title: 'Social Platforms',
                                platforms: socialPlatforms
                            } as SocialPlatformsContentType
                        },
                        {
                            type: WebsiteBuilderContentType.STREAMING_PLATFORMS,
                            streamingPlatformsContent: {
                                title: 'Listen Before You Come',
                                platforms: platformsLocal,
                                style: StreamingPlatformsStyle.ICONS,
                            } as StreamingPlatformsContentType
                        },
                        {
                            type: WebsiteBuilderContentType.TEXT,
                            textContent: {
                                title: 'Upcoming Tour Dates',
                                subtitle: 'Don\'t miss your chance to see the show',
                                isSubtitle: true,
                            } as TextContentType
                        },
                        {
                            type: WebsiteBuilderContentType.TOUR,
                            tourContent: {
                                title: `Tour ${Date.now()}`,
                                tourDates: [
                                    {
                                        date: dayjs().add(7, 'day').format('YYYY-MM-DD'),
                                        city: 'New York',
                                        country: 'United States',
                                        venue: 'Madison Square Garden',
                                        ticketUrl: 'https://www.ticketmaster.com/event/1234567890',
                                    },
                                    {
                                        date: dayjs().add(14, 'day').format('YYYY-MM-DD'),
                                        city: 'Los Angeles',
                                        country: 'United States',
                                        venue: 'Staples Center',
                                        ticketUrl: 'https://www.ticketmaster.com/event/1234567890',
                                    },
                                    {
                                        date: dayjs().add(21, 'day').format('YYYY-MM-DD'),
                                        city: 'Chicago',
                                        country: 'United States',
                                        venue: 'United Center',
                                        ticketUrl: 'https://www.ticketmaster.com/event/1234567890',
                                    }
                                ] as TourDate[],
                                hidePastTourDates: false,
                                style: TourStyle.STACKED,
                            } as TourContentType
                        }
                    ] as WebsiteBuilderContent[]

                    return {
                        title: templateTitle,
                        type: templateType,
                        websiteContent: websiteContentLocal,
                        websiteStyle: websiteStyleLocal
                    }
                case WebsiteBuilderTemplateType.PRESAVE:
                    websiteContentLocal = [
                        {
                            type: WebsiteBuilderContentType.IMAGE,
                            imageContent: {
                                title: `Image ${Date.now()}`,
                                image: brandImage,
                                style: ImageContentStyle.SQUARE,
                            } as ImageContentType
                        },
                        {
                            type: WebsiteBuilderContentType.TEXT,
                            textContent: {
                                title: `${brandName} is coming soon!`,
                                subtitle: `Presave ${brandName}'s music`,
                                isSubtitle: true
                            } as TextContentType
                        },
                        {
                            type: WebsiteBuilderContentType.PRESAVE,
                            presaveContent: {
                                title: `Presave ${Date.now()}`,
                                style: PresaveWebsiteBuilderStyle.BOXES,
                                presave: {
                                    boosts: {
                                        auto_presaves: false,
                                        boost_follows: false,
                                        collect_emails: false,
                                    },
                                    platforms: [
                                        {
                                            name: PresavePlatform.SPOTIFY,
                                            isEnabled: true,
                                        },
                                        {
                                            name: PresavePlatform.APPLE_MUSIC,
                                            isEnabled: true,
                                        },
                                    ],
                                    release_date: dayjs().format('YYYY-MM-DD'),
                                    timezone: 'America/New_York',
                                } as PresaveWebsiteType
                            } as PresaveContentType
                        }
                    ] as WebsiteBuilderContent[]

                    return {
                        title: templateTitle,
                        type: templateType,
                        websiteContent: websiteContentLocal,
                        websiteStyle: websiteStyleLocal
                    }
                default:
                    return null;
            }
        },
    }))
);

export const getWebsiteBuilderContentInfo = (type: WebsiteBuilderContentType) => {
    switch (type) {
        case WebsiteBuilderContentType.COLLECT_DATA:
            return {
                title: 'Collect Data',
            };
        case WebsiteBuilderContentType.DIVIDER:
            return {
                title: 'Divider',
            };
        case WebsiteBuilderContentType.EXTERNAL_LINKS:
            return {
                title: 'External Links',
            };
        case WebsiteBuilderContentType.IMAGE:
            return {
                title: 'Image',
            };
        case WebsiteBuilderContentType.MUSIC:
            return {
                title: 'Music Player',
            };
        case WebsiteBuilderContentType.PAYMENT:
            return {
                title: 'Payment',
            };
        case WebsiteBuilderContentType.PRESAVE:
            return {
                title: 'Presave',
            };
        case WebsiteBuilderContentType.SOCIAL_PLATFORMS:
            return {
                title: 'Social Platforms',
            };
        case WebsiteBuilderContentType.STREAMING_PLATFORMS:
            return {
                title: 'Streaming Platforms',
            };
        case WebsiteBuilderContentType.TABS:
            return {
                title: 'Tabs',
            };
        case WebsiteBuilderContentType.TEXT:
            return {
                title: 'Text',
            };
        case WebsiteBuilderContentType.TOUR:
            return {
                title: 'Tour',
            };
        case WebsiteBuilderContentType.VIDEO:
            return {
                title: 'Videos',
            };
        default:
            return {
                title: 'Unknown',
            };
    }
}

export const getStreamingLinksFromRecord = (recordDetails: RecordDetails | null): PlatformItem[] => {
    if (!recordDetails) {
        return [
            {
                title: 'Spotify',
                description: 'Listen to the song on Spotify',
                name: StreamingPlatform.SPOTIFY,
                icon: 'spotify',
                link: '',
                isEnabled: true,
                placeholder: '',
            }
        ];
    }

    const externalLinks = recordDetails.external_links;
    const platformsLocal: PlatformItem[] = externalLinks
        .filter(platform => {
            const platformName = platform.name.toLowerCase();
            return platformName !== StreamingPlatform.NAPSTER &&
                platformName !== StreamingPlatform.SHAZAM &&
                platformName !== StreamingPlatform.PANDORA &&
                platformName !== StreamingPlatform.BOOMPLAY;
        })
        .map((platform) => {
            const platformName = platform.name.toLowerCase();
            const {
                icon,
                title,
            } = STREAMING_PLATFORMS_INFO[platformName] || {};

            return {
                title: title || platform.name,
                description: `Listen to the song on ${platform.name}`,
                name: platformName,
                icon: icon || platformName,
                link: platform.url || '',
                isEnabled: true,
                placeholder: platform.url || `https://example.com/${platformName}`,
            };
        })

    return platformsLocal;
}

export const hasMusicPreview = (selectedMusic: RecordDetails | undefined | null) => {
    if (!selectedMusic) {
        return false;
    }

    const isAlbumOrPlaylist = selectedMusic?.spotify_release_type?.toLowerCase() === 'album' || selectedMusic?.spotify_release_type?.toLowerCase() === 'playlist';

    if (isAlbumOrPlaylist) {
        if (selectedMusic?.tracks) {
            for (let i = 0; i < selectedMusic.tracks.length; i++) {
                if (selectedMusic.tracks[i].preview_url) {
                    return true;
                }
            }
        }
    }

    if (selectedMusic?.preview_url) {
        return true;
    }

    return false;
}

export const getWebsiteBuilderPath = (brandSlug: string, websiteSlug: string) => {
    return `/website-builder/${brandSlug}/${websiteSlug}`;
}


export default useWebsiteBuilder;
